import { useMemo, useCallback, useEffect } from 'react';
import { UseFormReturn, useWatch, FieldValues } from 'react-hook-form';

import { formLocalStorage } from './formLocalStorage';
import { formMemoryStorage } from './formMemoryStorage';
import { IFormStorage } from './types';

type TFormStorageType = 'none' | 'ls' | 'memory';

const dummyStorage = {
  clear: () => {
    /* noop */
  },
  getItem: () => null,
  setItem: () => {
    /* noop */
  },
  removeItem: () => {
    /* noop */
  },
};

type TFormStorageConfig = {
  storeId: string;
  type: TFormStorageType;
};

const useFormStorage = <TFieldValues extends FieldValues>({
  config: { storeId, type },
  form,
}: {
  config: TFormStorageConfig;
  form: UseFormReturn<TFieldValues>;
}) => {
  const values = useWatch({ control: form.control });

  const storage = useMemo<IFormStorage<TFieldValues>>(() => {
    switch (type) {
      case 'memory':
        return formMemoryStorage;
      case 'ls':
        return formLocalStorage;
      default:
        return dummyStorage;
    }
  }, [type]);

  useEffect(() => {
    const presavedValues = storage.getItem(storeId);

    if (presavedValues) {
      form.reset(presavedValues);
    }
  }, [form, storage, storeId]);

  useEffect(() => {
    storage.setItem(storeId, values);
  }, [storeId, values, storage]);

  const clearStorage = useCallback(() => {
    storage.removeItem(storeId);
  }, [storeId, storage]);

  return clearStorage;
};

export { useFormStorage };
export type { IFormStorage, TFormStorageType, TFormStorageConfig };
